import { push } from '@lagunovsky/redux-react-router'
import React, { useEffect } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import ProdGraphPanel from 'productionUnit/components/ProdGraphPanel'
import ThresholdIndicators from 'piezometry/components/alerts/thresholds/ThresholdIndicators'
import { Box, Grid2 } from '@mui/material'
import useActions from 'utils/customHook/useActions'
import useTitle from 'utils/customHook/useTitle'
import { useParams } from 'react-router'
import { componentHasHabilitations } from 'utils/HabilitationUtil'
import { H_MAT_MODULE, H_UNITS_DASHBOARD } from 'account/constants/AccessRulesConstants'
import { getLinks, getStationTitle } from 'utils/StationUtils'
import { getStationArrowNav } from 'utils/ActionUtils'
import UnitChartPanel from './UnitChartPanel'
import StationMapDashboardPanel from 'station/components/dashboard/component/map/StationMapDashboardPanel'
import StationDescription from 'station/components/description/StationDescription'
import HomeAction from 'home/actions/HomeAction'
import UnitAction from 'referencial/components/unit/actions/UnitAction'
import ProductionPiezometerAlertPanel from './alerts/ProductionPiezometerAlertPanel'
import HabilitationRequired from 'utils/components/HabilitationRequired'
import MaterielAssigned from 'station/components/materiel/MaterielAssigned'
import LastEventPanel from 'station/components/event/LastEventPanel'
import SmallPicturePanel from 'components/picture/SmallPicturePanel'
import FilePanel from 'components/file/FilePanel'
import HydrogeologicalEntityAction from 'referencial/components/hydrogeologicalEntity/actions/HydrogeologicalEntityAction'
import CampaignAction from 'campaign/actions/CampaignAction'
import QualityAction from 'quality/actions/QualityAction'
import { STATION_TYPE_NAME } from 'station/constants/StationConstants'
import ProductionUnitLinkedPrel from 'productionUnit/components/ProductionUnitLinkedPrel'

const ProductionUnitDashboard = ({
}) => {
    const dispatch = useDispatch()
    const { id } = useParams()

    const {
        productionUnit,
        pictures,
        files,
        units,
        productionUnitEvents,
        productionUnits,
        measures,
        stationCentral,
        stationPowerSupply,
        stationSensor,
        stationSim,
        stationTelecom,
        stationVariousMateriel,
        stationEquipment,
        stationSubscriptionAssignments,
    } = useSelector(store => ({
        productionUnit: store.ProductionUnitReducer.productionUnit,
        pictures: store.StationReducer.pictures,
        files: store.StationReducer.files,
        units: store.UnitReducer.units,
        productionUnitEvents: store.ProductionUnitReducer.productionUnitEvents,
        productionUnits: store.ProductionUnitReducer.productionUnits,
        measures: store.PiezometerStationReducer.measures,
        stationCentral: store.StationReducer.stationCentral,
        stationPowerSupply: store.StationReducer.stationPowerSupply,
        stationSensor: store.StationReducer.stationSensor,
        stationSim: store.StationReducer.stationSim,
        stationTelecom: store.StationReducer.stationTelecom,
        stationVariousMateriel: store.StationReducer.stationVariousMateriel,
        stationEquipment: store.StationReducer.stationEquipment,
        stationSubscriptionAssignments: store.StationReducer.stationSubscriptionAssignments,
    }), shallowEqual)

    useEffect(() => {
        const isAuthorized = componentHasHabilitations(H_UNITS_DASHBOARD)
        if (!isAuthorized) { // A modifier quand react-router sera à jour
            push('/unauthorized')
            return
        }
        dispatch(HomeAction.setBackPath('/productionUnit'))
        dispatch(UnitAction.fetchUnits())
        dispatch(QualityAction.fetchThresholds())
        dispatch(HydrogeologicalEntityAction.fetchHydrogeologicalEntities())
        dispatch(CampaignAction.fetchPiezometryCampaignsProgress())
    }, [])

    useActions(() => {
        return {
            links: getLinks(productionUnit, {}),
            arrowNav: getStationArrowNav(STATION_TYPE_NAME.productionUnit, productionUnits, productionUnit.id, s => dispatch(push(`/station/productionUnit/${s.id}`))),
        }
    }, [productionUnit, units, productionUnits])

    useTitle(() => [{
        title: i18n.productionUnit,
        href: STATION_TYPE_NAME.productionUnit,
    }, {
        title: getStationTitle(productionUnit),
        href: `station/productionUnit/${productionUnit.id}`,
    }], [productionUnit])

    const onShowMore = () => {
        push(`/station/productionUnit/${productionUnit.id}/picture`)
    }

    return (
        <Box sx={{ paddingBottom: '150px', paddingLeft: '20px' }}>
            <Grid2 container alignItems='flex-start' sx={{ paddingTop: '10px' }} spacing={1}>
                <Grid2 container size={9}>
                    <Grid2 size={12}>
                        <StationDescription id={id} stationTypes={[STATION_TYPE_NAME.productionUnit]} noOwner />
                    </Grid2>
                    <Grid2 size={12}>
                        <ProdGraphPanel />
                    </Grid2>
                    <UnitChartPanel id={productionUnit.id} productionUnit={productionUnit} fromStationType='PROD' />
                    <Grid2 size={12}>
                        <ProductionUnitLinkedPrel />
                    </Grid2>
                </Grid2>
                <Grid2 container size={3} spacing={1}>
                    { !!measures.legnth && (
                        <Grid2 size={12}>
                            <ProductionPiezometerAlertPanel />
                        </Grid2>
                    ) }
                    { !!(stationCentral.length || stationPowerSupply.length || stationSensor.length
                            || stationSim.length || stationTelecom.length || stationVariousMateriel.length
                            || stationEquipment.length || stationSubscriptionAssignments.length)
                            && (
                                <HabilitationRequired habilitation={H_MAT_MODULE}>
                                    <Grid2 size={12}>
                                        <MaterielAssigned stationId={id} type={STATION_TYPE_NAME.productionUnit} />
                                    </Grid2>
                                </HabilitationRequired>
                            )
                    }
                    { !!productionUnitEvents.length && (
                        <Grid2 size={12}>
                            <LastEventPanel
                                id={id}
                                events={productionUnitEvents}
                                stationId={id}
                                stationType={STATION_TYPE_NAME.productionUnit}
                            />
                        </Grid2>
                    )}
                    <Grid2 size={12}>
                        <ThresholdIndicators/>
                    </Grid2>
                    <Grid2 size={12}>
                        <StationMapDashboardPanel
                            noMarkerTooltip
                            station={productionUnit}
                            type={STATION_TYPE_NAME.productionUnit}
                        />
                    </Grid2>
                    {!!pictures?.length && (
                        <Grid2 size={12}>
                            <SmallPicturePanel element={'station'} pictures={pictures} />
                        </Grid2>
                    )}
                    {!!files?.length && (
                        <Grid2 size={12}>
                            <FilePanel code={productionUnit.code} files={files} hideTitle onShowMore={onShowMore} />
                        </Grid2>
                    )}
                </Grid2>
            </Grid2>
        </Box>
    )
}

ProductionUnitDashboard.propTypes = {
}

export default ProductionUnitDashboard
